/**
 * DelegationPanel — slide-in panel for delegating a step.
 * Per portal-phases.md Phase 3.7: assign to collaborator or invite new email,
 * role for step (Doer/Reviewer/Approver), optional message, notify options.
 */

import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Divider,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

export type StepRole = 'doer' | 'reviewer' | 'approver';

interface DelegationPanelProps {
  open: boolean;
  stepTitle: string;
  onClose: () => void;
  onAssign: (data: {
    assignee: string;
    role: StepRole;
    message: string;
    notifyEmail: boolean;
    notifyInApp: boolean;
  }) => void;
}

export const DelegationPanel: React.FC<DelegationPanelProps> = ({
  open,
  stepTitle,
  onClose,
  onAssign,
}) => {
  const [assignee, setAssignee] = useState('');
  const [role, setRole] = useState<StepRole>('doer');
  const [message, setMessage] = useState('');
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifyInApp, setNotifyInApp] = useState(true);

  const handleSubmit = () => {
    if (!assignee.trim()) return;
    onAssign({ assignee: assignee.trim(), role, message, notifyEmail, notifyInApp });
    // reset
    setAssignee('');
    setRole('doer');
    setMessage('');
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 400 } } }}>
      <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="overline" color="text.secondary">
              Delegate
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {stepTitle}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Stack spacing={2.5} sx={{ flex: 1 }}>
          {/* Assignee */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Assign to
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Search collaborators or enter email..."
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
            />
          </Box>

          {/* Role */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Role for this step
            </Typography>
            <RadioGroup value={role} onChange={(e) => setRole(e.target.value as StepRole)}>
              <FormControlLabel value="doer" control={<Radio size="small" />} label="Doer — completes the step" />
              <FormControlLabel value="reviewer" control={<Radio size="small" />} label="Reviewer — approves completion" />
              <FormControlLabel value="approver" control={<Radio size="small" />} label="Approver — final sign-off" />
            </RadioGroup>
          </Box>

          {/* Message */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Message (optional)
            </Typography>
            <TextField
              fullWidth
              size="small"
              multiline
              rows={2}
              placeholder="Please complete by Friday..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </Box>

          {/* Notifications */}
          <Box>
            <FormControlLabel
              control={<Checkbox size="small" checked={notifyEmail} onChange={(e) => setNotifyEmail(e.target.checked)} />}
              label="Notify via email"
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={notifyInApp} onChange={(e) => setNotifyInApp(e.target.checked)} />}
              label="Notify in-app"
            />
          </Box>
        </Stack>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={!assignee.trim()}>
            Assign step
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default DelegationPanel;
